import PropTypes from 'prop-types';

import {Flex, Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';

/**
 * MediaContentSection (Text Image / Media Content Section)
 *
 * Renders media (Image or Video) with optional heading and body. Figma: Desktop 11835-54399 (Good design award), Mobile 11830-60372.
 * Desktop: row, media 510×390, gap 40, padding 80 80 120; text region gap 40 between heading and body.
 * Mobile: column, text then image 280×280, gap 16 (heading/body), 40 (header/image), padding 56 20.
 * Contentful: heading for section title (only field rendered as title); body for description.
 */
const MEDIA_TYPES = ['Image', 'Video'];

export const MediaContentSection = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {heading, bodyCopy, body, media, mediaAlignment} = component ?? {};
  const bodyText = bodyCopy ?? body;
  const displayHeading =
    typeof heading === 'string' && heading.trim().length > 0 ? heading.trim() : null;

  const normalizedAlignment = mediaAlignment?.toLowerCase() || 'left';
  const styles = useMultiStyleConfig('contentful/media-content-section', {
    component,
    isComplete,
    isFetching,
    variant: normalizedAlignment,
  });

  // Only render Image or Video (Icon is handled by CF-054)
  const mediaItem = media && MEDIA_TYPES.includes(media.__typename) ? media : null;
  const showHeading = Boolean(displayHeading);
  const showBody = Boolean(bodyText);
  const showMedia = Boolean(mediaItem);
  const hasContent = showHeading || showBody || showMedia;

  if (!hasContent) {
    return null;
  }

  const textContent = (
    <Box {...styles.textRegion}>
      {showHeading && (
        <Text {...styles.heading}>{displayHeading}</Text>
      )}
      {showBody && (
        <Text {...styles.body}>{bodyText}</Text>
      )}
    </Box>
  );

  const mediaContent = showMedia && (
    <Box {...styles.mediaRegion}>
      <Component
        component={mediaItem}
        isComplete={isComplete}
        isFetching={isFetching}
      />
    </Box>
  );

  return (
    <Flex {...styles.container} {...props}>
      <Flex {...styles.inner}>
        {normalizedAlignment === 'right' ? (
          <>
            {textContent}
            {mediaContent}
          </>
        ) : (
          <>
            {mediaContent}
            {textContent}
          </>
        )}
      </Flex>
    </Flex>
  );
};

MediaContentSection.displayName = 'MediaContentSection';
MediaContentSection.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
